// src/Homepage/Navbar.js
import React, { useState, useEffect, useContext } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import {
  MapPin, Phone, Truck, ClipboardList, Heart, User, Search,
  MessageSquare, ShoppingCart, ChevronDown, Menu, X, HelpCircle, LogOut
} from "lucide-react";
import { CartContext } from "../components/CartContext";
import { API_BASE_URL } from "../config";

const Navbar = () => {
  const [user, setUser] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [adminLoginOpen, setAdminLoginOpen] = useState(false);
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [categoriesList, setCategoriesList] = useState(["All"]);
  const [allCategoriesOpen, setAllCategoriesOpen] = useState(false);

  const navigate = useNavigate();

  // Consume Shared Search/Category/Cart context
  const {
    cartItems,
    calculateOverallTotals,
    searchTerm,
    setSearchTerm,
    selectedCategory,
    setSelectedCategory,
    wishlist,
    setIsWishlistOpen
  } = useContext(CartContext);

  const { overallTotal } = calculateOverallTotals();

  useEffect(() => {
    // Fetch user session
    const savedUser = localStorage.getItem("user");
    if (savedUser) {
      setUser(JSON.parse(savedUser));
    }

    // Fetch dynamic categories list
    axios.get(`${API_BASE_URL}/all-data`)
      .then((response) => {
        if (response.data) {
          const cats = ["All", ...new Set(response.data.map((p) => p.name).filter(Boolean))];
          setCategoriesList(cats);
        }
      })
      .catch((err) => {
        console.error("Error fetching categories in Navbar:", err);
      });
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    setUser(null);
    setProfileOpen(false);
    setMobileOpen(false);
    navigate("/");
  };

  const handleDashboard = () => {
    navigate(user?.role === "admin" ? "/admin" : "/user-dashboard");
    setProfileOpen(false);
    setMobileOpen(false);
  };

  const handleAdminLogin = () => {
    if (!adminEmail || !adminPassword) {
      setLoginError("Please enter email and password");
      return;
    }
    axios.post(`${API_BASE_URL}/check-login`, {
      LOGIN_EMAIL: adminEmail,
      LOGIN_PASSWORD: adminPassword
    })
      .then((res) => {
        if (res.data && res.data.success) {
          const u = res.data.user;
          const role = u.ROLE || u.role || "user";
          if (role === "admin") {
            localStorage.setItem("user", JSON.stringify({ name: u.LOGIN_NAME, email: u.LOGIN_EMAIL, role: role }));
            localStorage.setItem("token", res.data.token);
            setUser({ name: u.LOGIN_NAME, email: u.LOGIN_EMAIL, role: role });
            setAdminLoginOpen(false);
            setAdminEmail("");
            setAdminPassword("");
            setLoginError("");
            navigate("/admin");
          } else {
            setLoginError("Access denied: Admin privileges required.");
          }
        } else {
          setLoginError(res.data.error || "Invalid email or password");
        }
      })
      .catch((err) => {
        setLoginError(err.response?.data?.error || "Connection error. Please try again.");
      });
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    navigate("/");
  };

  const handleCategorySelect = (cat) => {
    setSelectedCategory(cat);
    setAllCategoriesOpen(false);
    navigate("/");
  };

  return (
    <header className="w-full z-[1000] relative font-sans select-none">

      {/* ROW 1: TOP NOTICE BAR */}
      <div className="bg-[#500707] text-white text-[11px] font-semibold py-2.5 px-4 sm:px-6 lg:px-8 border-b border-[#3c0404] shadow-sm flex flex-col sm:flex-row justify-between items-center gap-2">
        <div className="flex flex-wrap items-center gap-4 justify-center sm:justify-start">
          <div className="flex items-center gap-1.5 text-gray-200">
            <MapPin size={13} className="text-yellow-400" />
            <span>Sivakasi Wholesale Firecrackers Direct to Your Door</span>
          </div>
          <div className="flex items-center gap-1.5 text-gray-200">
            <Phone size={13} className="text-yellow-400" />
            <span>Helpline Support: <a href="tel:+919728622667" className="hover:underline text-yellow-300 font-bold">+91 9728622667</a></span>
          </div>
          <div className="flex items-center gap-1.5 text-gray-200">
            <Truck size={13} className="text-yellow-400" />
            <span>Fast & Safe Delivery Across India</span>
          </div>
        </div>

        <div className="flex items-center gap-4 text-gray-200">
          <Link to={user ? "/user-dashboard" : "/loginRegistor"} className="flex items-center gap-1 hover:text-yellow-300 transition">
            <ClipboardList size={13} />
            <span>Track Order</span>
          </Link>
          <span className="text-[#6e1515]">|</span>
          <button onClick={() => setIsWishlistOpen(true)} className="flex items-center gap-1 hover:text-yellow-300 transition relative">
            <Heart size={13} className="text-yellow-400" />
            <span>My Wishlist</span>
            {wishlist && wishlist.length > 0 && (
              <span className="bg-red-650 text-white text-[9px] font-black px-1.5 py-0.5 rounded-full ml-1">
                {wishlist.length}
              </span>
            )}
          </button>
          <span className="text-[#6e1515]">|</span>

          {user ? (
            <div className="relative">
              <button
                onClick={() => setProfileOpen(!profileOpen)}
                className="flex items-center gap-1 hover:text-yellow-300 transition font-bold"
              >
                <User size={13} className="text-yellow-400" />
                <span className="capitalize">{user.name}</span>
                <ChevronDown size={11} />
              </button>
              {profileOpen && (
                <div className="absolute right-0 mt-2 w-44 bg-white text-gray-800 rounded-lg shadow-xl py-1 border border-gray-100 z-50">
                  <button onClick={handleDashboard} className="w-full text-left px-4 py-2 text-xs font-bold hover:bg-red-50 hover:text-[#500707] transition">
                    {user.role === "admin" ? "⚙️ Admin Controls" : "👤 My Dashboard"}
                  </button>
                  <button onClick={handleLogout} className="w-full text-left px-4 py-2 text-xs font-bold text-red-600 hover:bg-red-50 transition border-t">
                    <span className="flex items-center gap-1"><LogOut size={12} /> Log Out</span>
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link to="/loginRegistor" className="flex items-center gap-1 hover:text-yellow-300 transition">
              <User size={13} />
              <span>Login / Register</span>
            </Link>
          )}
        </div>
      </div>

      {/* ROW 2: MIDDLE HEADER */}
      <div className="bg-white py-4 px-4 sm:px-6 lg:px-8 border-b border-gray-100 shadow-xs">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">

          {/* Logo & Tagline */}
          <div className="flex items-center gap-3 cursor-pointer select-none" onClick={() => { setSearchTerm(""); setSelectedCategory("All"); navigate("/"); }}>
            <div className="w-12 h-12 bg-purple-900 rounded-full flex items-center justify-center text-2xl shadow-md border border-purple-800">
              🎇
            </div>
            <div>
              <h1 className="text-2xl font-black text-[#500707] tracking-tight font-serif leading-none">
                மகிழ்யா பட்டாசுகள்
              </h1>
              <p className="text-[10px] text-gray-500 font-semibold tracking-wide italic mt-1 font-sans">
                Celebrate Safety, Celebrate Happiness
              </p>
            </div>
          </div>

          {/* Unified Search Control */}
          <form onSubmit={handleSearchSubmit} className="flex flex-1 max-w-2xl w-full border border-gray-300 rounded-lg overflow-hidden shadow-xs hover:shadow-sm focus-within:ring-2 focus-within:ring-[#500707]/30 transition-all font-sans text-sm">
            <input
              type="text"
              placeholder="Search for crackers, sparklers, combo..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 px-4 py-2.5 focus:outline-none bg-white text-gray-800"
            />
            <div className="relative border-l border-gray-200 bg-gray-50 flex items-center">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-3 pr-8 py-2.5 bg-transparent focus:outline-none cursor-pointer font-bold text-gray-650 appearance-none"
              >
                {categoriesList.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
            <button type="submit" className="bg-[#500707] hover:bg-[#6c0b0b] text-white px-6 py-2.5 flex items-center justify-center transition">
              <Search size={16} />
            </button>
          </form>

          {/* Support Widget & Cart Widget */}
          <div className="flex items-center gap-6 justify-end w-full md:w-auto">
            {/* WhatsApp support link */}
            <a
              href="https://wa.me/919728622667"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 hover:opacity-95 transition"
            >
              <div className="w-10 h-10 bg-green-50 rounded-full flex items-center justify-center border border-green-200">
                <span className="text-green-600 text-lg font-black font-sans">📞</span>
              </div>
              <div className="text-left font-sans">
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider leading-none">WhatsApp Support</p>
                <p className="text-xs font-black text-gray-700 mt-1">+91 9728622667</p>
              </div>
            </a>

            {/* My Cart Outline Box */}
            <button
              onClick={() => navigate("/")}
              className="flex items-center gap-3 border border-gray-200 px-4 py-2 rounded-xl bg-gray-50/50 hover:bg-gray-50 hover:border-gray-300 transition text-left"
            >
              <div className="relative text-red-700 bg-red-50 p-2 rounded-lg border border-red-150">
                <ShoppingCart size={18} />
                {cartItems.length > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 bg-[#500707] text-white text-[9px] font-black rounded-full w-4 h-4 flex items-center justify-center border border-white">
                    {cartItems.reduce((sum, item) => sum + (item.quantity || 0), 0)}
                  </span>
                )}
              </div>
              <div className="font-sans">
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider leading-none">My Cart</p>
                <p className="text-xs font-black text-gray-800 mt-1">₹{overallTotal || 0}</p>
              </div>
            </button>
          </div>

        </div>
      </div>

      {/* ROW 3: CATEGORY & SECONDARY NAV MENU */}
      <div className="bg-[#7A0D0D] text-white text-sm font-sans shadow-md">
        <div className="max-w-7xl mx-auto flex justify-between items-center relative">

          <div className="flex items-center">
            {/* Category Dropdown Toggler */}
            <div className="relative">
              <button
                onClick={() => setAllCategoriesOpen(!allCategoriesOpen)}
                className="bg-yellow-400 text-red-950 hover:bg-yellow-500 font-black px-6 py-3.5 flex items-center gap-2.5 transition text-xs uppercase tracking-wider select-none"
              >
                <Menu size={16} />
                <span>All Categories</span>
                <ChevronDown size={14} />
              </button>

              {allCategoriesOpen && (
                <ul className="absolute left-0 mt-0.5 w-56 bg-white text-gray-800 rounded-b-xl shadow-2xl py-2 border border-gray-150 z-50 animate-fade-in font-bold text-xs uppercase">
                  {categoriesList.map(cat => (
                    <li
                      key={cat}
                      onClick={() => handleCategorySelect(cat)}
                      className={`px-5 py-3 hover:bg-red-50 hover:text-[#500707] cursor-pointer transition ${selectedCategory === cat ? "bg-red-50 text-[#500707]" : ""}`}
                    >
                      🎇 {cat}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Navigation links */}
            <nav className="hidden lg:flex items-center gap-8 pl-8 font-black uppercase text-xs tracking-wider text-red-100">
              <Link to="/" onClick={() => { setSearchTerm(""); setSelectedCategory("All"); }} className="hover:text-yellow-300 transition">Home</Link>
              <Link to="/" onClick={() => { setSearchTerm(""); setSelectedCategory("All"); }} className="hover:text-yellow-300 transition">Shop</Link>
              <Link to="/" onClick={() => { setSearchTerm(""); setSelectedCategory("All"); }} className="hover:text-yellow-300 transition flex items-center gap-1.5">
                <span>Combo Offers</span>
                <span className="bg-yellow-400 text-[#500707] text-[8px] font-black px-1.5 py-0.5 rounded-md animate-pulse">HOT</span>
              </Link>
              <Link to="/" className="hover:text-yellow-300 transition">New Arrivals</Link>
              <Link to="/bulk-shop" className="hover:text-yellow-300 transition">Bulk Orders</Link>
              <Link to="/safetips" className="hover:text-yellow-300 transition">Safety Tips</Link>
              <Link to="/contact" className="hover:text-yellow-300 transition">Contact Us</Link>
            </nav>
          </div>

          <div className="flex items-center pr-4">
            {/* Quick Admin Access Button */}
            <button
              onClick={() => {
                if (user?.role === "admin") {
                  navigate("/admin");
                } else {
                  setAdminLoginOpen(true);
                }
              }}
              className="text-xs font-black uppercase tracking-wider text-yellow-300 hover:text-white transition flex items-center gap-1 bg-[#610a0a] px-3.5 py-1.5 rounded-lg border border-[#8f1818]"
            >
              <span>⚙️</span> Supervisor Login
            </button>
          </div>

        </div>
      </div>

      {/* ADMIN LOGIN MODAL BOX */}
      {adminLoginOpen && (
        <>
          <div className="fixed inset-0 bg-black/60 backdrop-blur-xs z-[2000]" onClick={() => setAdminLoginOpen(false)} />
          <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-3xl p-6 w-80 z-[2050] shadow-2xl border border-orange-50 animate-fade-in font-sans">
            <h2 className="text-xl font-black text-[#500707] mb-1 font-serif">Admin Login</h2>
            <p className="text-xs text-gray-500 mb-4">Provide wholesale store supervisor credentials</p>
            {loginError && <p className="text-red-600 text-xs font-semibold mb-3">⚠️ {loginError}</p>}
            <div className="mb-3.5">
              <label className="block text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-1">Email Address</label>
              <input
                type="email"
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
                className="w-full px-3.5 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#500707] bg-white text-gray-800"
                placeholder="admin@example.com"
              />
            </div>
            <div className="mb-4">
              <label className="block text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-1">Security Password</label>
              <input
                type="password"
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
                className="w-full px-3.5 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#500707] bg-white text-gray-800"
                placeholder="••••••••"
              />
            </div>
            <button
              onClick={handleAdminLogin}
              className="w-full bg-[#500707] hover:bg-[#6c0b0b] text-white py-2.5 rounded-xl font-bold transition text-xs uppercase tracking-wider"
            >
              Sign In
            </button>
            <button
              onClick={() => setAdminLoginOpen(false)}
              className="w-full mt-2.5 text-xs text-gray-505 hover:text-red-900 hover:underline font-semibold text-center text-gray-500"
            >
              Cancel
            </button>
          </div>
        </>
      )}
    </header>
  );
};

export default Navbar;